import { Component } from '@nicolas-riciotti/light2';

const VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
}`;

// Full homepage-style chain in a single pass: vignette (with skew/angle/
// position) → sine UV warp → shatter (voronoi displacement) → bokeh (golden-
// angle highlight-weighted blur). No mouse uniforms. Outputs premultiplied
// RGB so screen-blend mode can lift the bg without any dark tint.
const FRAGMENT_SHADER = `
precision mediump float;
#define PI 3.14159265
#define PI2 6.28318530718
#define PI3 1.04709283
#define GOLDEN_ANGLE 2.39996323

uniform float u_time;
uniform vec2  u_resolution;
uniform vec3  u_color;

// Vignette
uniform float u_vig_radius;
uniform float u_vig_falloff;
uniform float u_vig_skew;
uniform float u_vig_angle;
uniform float u_vig_pos_x;
uniform float u_vig_pos_y;

// Sine
uniform float u_sine_freq;
uniform float u_sine_amp;
uniform float u_sine_speed;

// Shatter
uniform float u_shatter_scale;
uniform float u_shatter_spread;
uniform float u_shatter_angle;
uniform float u_shatter_skew;

// Bokeh
uniform float u_bokeh_radius;
uniform float u_bokeh_tilt;
uniform float u_bokeh_amount;
uniform int   u_bokeh_iters;

// Final mix
uniform float u_radial_inner;
uniform float u_radial_outer;
uniform float u_power;
uniform float u_gamma;
uniform float u_brightness;

vec2 random2(vec2 p) {
    return fract(sin(vec2(dot(p, vec2(127.1, 311.7)),
                          dot(p, vec2(269.5, 183.3)))) * 43758.5453);
}

mat2 rot(float a) {
    return mat2(cos(a), -sin(a), sin(a), cos(a));
}

// Vignette field — homepage's radial vignette with angle/skew/position.
float vignette(vec2 uv, float aspect) {
    vec2 ar = vec2(aspect, 1.0);
    vec2 sk = vec2(u_vig_skew, 1.0 - u_vig_skew);
    float ang = u_vig_angle * PI2;
    vec2 pos = vec2(u_vig_pos_x, u_vig_pos_y);

    float halfR  = u_vig_radius * 0.5;
    float innerE = halfR - u_vig_falloff * halfR * 0.5;
    float outerE = halfR + u_vig_falloff * halfR * 0.5;

    vec2 sUV  = uv  * ar * rot(ang) * sk;
    vec2 sPos = pos * ar * rot(ang) * sk;
    float r = distance(sUV, sPos);
    float f = smoothstep(innerE, outerE, r);
    return 1.0 - f; // 1 at center, 0 at edges
}

// Sine UV warp — same shape as BlockGL.js's sine shader pass.
vec2 sineWarp(vec2 uv) {
    vec2 wc = uv * 2.0 - 1.0;
    float t = u_time * u_sine_speed;
    float waveX = sin(wc.y * u_sine_freq + t * PI3)       * (u_sine_amp * 0.2);
    float waveY = sin(wc.x * u_sine_freq + t * PI3 * 1.3) * (u_sine_amp * 0.2);
    wc += vec2(waveX, waveY);
    return wc * 0.5 + 0.5;
}

// Shatter — homepage's voronoi displacement. Returns a UV offset that gets
// applied before sampling the underlying field.
vec2 shatterOffset(vec2 uv, float aspect) {
    vec2 pos = vec2(0.5, 0.5);
    vec2 sk  = mix(vec2(1.0), vec2(1.0, 0.0), u_shatter_skew);
    vec2 st = (uv - pos) * vec2(aspect, 1.0) * 50.0 * u_shatter_scale;
    st = st * rot(u_shatter_angle * PI2) * sk;

    vec2 i_st = floor(st);
    vec2 f_st = fract(st);
    float m_dist = 15.0;
    vec2 m_point = vec2(0.0);
    for (int j = -1; j <= 1; j++) {
        for (int i = -1; i <= 1; i++) {
            vec2 neighbor = vec2(float(i), float(j));
            vec2 point = random2(i_st + neighbor);
            point = 0.5 + 0.5 * sin(5.0 + u_time * 0.2 + PI2 * point);
            vec2 diff = neighbor + point - f_st;
            float d = length(diff);
            if (d < m_dist) { m_dist = d; m_point = point; }
        }
    }
    return (m_point * 0.2 * u_shatter_spread * 2.0) - (u_shatter_spread * 0.2);
}

// Sample the warped+shattered vignette at a given UV — this is the "field"
// the bokeh pass blurs.
float sampleField(vec2 uv, float aspect) {
    vec2 warped = sineWarp(uv);
    vec2 shifted = warped + shatterOffset(warped, aspect);
    return vignette(shifted, aspect);
}

// Bokeh blur — golden-angle samples around uv, weighted toward highlights
// (pow(c, 9) * 150) like the homepage's bokeh material. iters and radius
// are tweakable from the toolbar.
float bokeh(vec2 uv, float aspect) {
    if (u_bokeh_iters <= 1) return sampleField(uv, aspect);

    float acc = 0.0;
    float wsum = 0.0;
    float r = 1.0;
    vec2 pxSize = vec2(1.0 / aspect, 1.0) * 0.04;

    // distance-based tilt so blur grows toward edges (homepage uTilt)
    float dis = distance(uv, vec2(0.5)) * 1000.0;
    float tilt = mix(1.0 - dis * 0.001, dis * 0.001, u_bokeh_tilt);
    float radius = u_bokeh_radius * tilt;

    const int MAX_ITERS = 32;
    for (int k = 0; k < MAX_ITERS; k++) {
        if (k >= u_bokeh_iters) break;
        float j = float(k) * GOLDEN_ANGLE;
        r += 1.0 / r;
        vec2 offset = (r - 1.0) * vec2(cos(j), sin(j)) * pxSize * radius * u_bokeh_amount;
        float v = sampleField(uv + offset, aspect);
        float w = 5.0 + pow(clamp(v, 0.0, 1.0), 9.0) * 150.0;
        acc  += v * w;
        wsum += w;
    }
    return acc / wsum;
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    float aspect = u_resolution.x / u_resolution.y;

    float v = bokeh(uv, aspect);
    v = pow(clamp(v, 0.0, 1.0), u_power);

    // Centered radial vignette so the noise reads as a soft blob.
    vec2 centered = (uv - 0.5) * vec2(aspect, 1.0);
    float radial = 1.0 - smoothstep(u_radial_inner, u_radial_outer, length(centered));

    float alpha = v * radial * u_brightness;
    alpha = pow(clamp(alpha, 0.0, 1.0), 1.0 / max(u_gamma, 0.0001));

    // Premultiplied output — pairs with gl.blendFunc(ONE, ONE_MINUS_SRC_ALPHA)
    // and mix-blend-mode: screen so the bg is only lifted, never darkened.
    gl_FragColor = vec4(u_color * alpha, alpha);
}`;

const DEFAULT_PARAMS = {
    // Vignette
    vigRadius:   0.335,
    vigFalloff:  1.54,
    vigSkew:     0.78,
    vigAngle:    0.27,
    vigPosX:     0.51,
    vigPosY:     0.455,
    // Sine
    sineFreq:    1.95,
    sineAmp:     0.47,
    sineSpeed:   0.175,
    // Shatter
    shatterScale:  0.95,
    shatterSpread: 2.15,
    shatterAngle:  0.095,
    shatterSkew:   0.84,
    // Bokeh
    bokehRadius: 0.98,
    bokehTilt:   0.72,
    bokehAmount: 1.01,
    bokehIters:  10,
    // Final mix
    radialInner: 0.205,
    radialOuter: 0.535,
    power:       1.15,
    gamma:       1.0,
    brightness:  0.45,
    blendMode:   'multiply',
    cssBlur:     10,
};

function hexToRgb(hex) {
    if (!hex) return [1, 0.66, 0.64];
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    return [
        parseInt(c.slice(0, 2), 16) / 255,
        parseInt(c.slice(2, 4), 16) / 255,
        parseInt(c.slice(4, 6), 16) / 255,
    ];
}

function compileShader(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn('VoiceEmotion shader compile error', gl.getShaderInfoLog(s));
        gl.deleteShader(s);
        return null;
    }
    return s;
}

export default class BlockVoiceEmotion extends Component {

    attached() {
        this.$canvas = this.querySelector('[data-voice-noise]');
        this.$voiceButtons = Array.from(this.querySelectorAll('[data-voice-color]'));

        this.params = { ...DEFAULT_PARAMS };

        this.onVoiceClick = this.onVoiceClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.tick = this.tick.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);

        this.$voiceButtons.forEach((btn) => {
            btn.addEventListener('click', this.onVoiceClick);
        });

        // Mouse-driven blob center. Default to center; we lerp toward the
        // current pointer position so the noise drifts toward the cursor
        // without snapping.
        this.targetMouse = { x: 0.5, y: 0.5 };
        this.currMouse   = { x: 0.5, y: 0.5 };
        this.addEventListener('mousemove', this.onPointerMove);
        this.addEventListener('mouseleave', () => {
            // Ease back to center when the pointer leaves the hero.
            this.targetMouse.x = 0.5;
            this.targetMouse.y = 0.5;
        });

        const initialHex = getComputedStyle(this.$canvas)
            .getPropertyValue('--blob-color').trim() || '#FFAAA5';
        this.currentColor = hexToRgb(initialHex);
        this.targetColor = this.currentColor.slice();

        this.intersecting = true;
        this.lastTickMs = null;
        this.elapsed = 0;

        if (!this.$canvas) return;
        this.applyBlendMode(this.params.blendMode);
        this.applyCssBlur(this.params.cssBlur);
        this.initGL();
        this.onResize();
        window.addEventListener('resize', this.onResize);

        if ('IntersectionObserver' in window) {
            this.io = new IntersectionObserver((entries) => {
                const nowVisible = entries.some(e => e.isIntersecting);
                if (nowVisible === this.intersecting) return;
                this.intersecting = nowVisible;
                if (nowVisible) this.resumeGL();
                else this.suspendGL();
            }, { rootMargin: '50px' });
            this.io.observe(this);
        }

        if (this.intersecting) {
            this.rafId = requestAnimationFrame(this.tick);
        }
    }

    // Pause the render loop when off-screen but keep the GL context alive.
    // The cost of recreating the context (shader recompile + buffer realloc)
    // showed up as a small scroll hiccup on lower-end hardware; keeping the
    // context warm trades a few MB of GPU memory for instant resume.
    suspendGL() {
        if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
        this.lastTickMs = null;
        this._suspended = true;
    }

    resumeGL() {
        this._suspended = false;
        if (!this.rafId) this.rafId = requestAnimationFrame(this.tick);
    }

    detached() {
        this.$voiceButtons?.forEach((btn) => {
            btn.removeEventListener('click', this.onVoiceClick);
        });
        window.removeEventListener('resize', this.onResize);
        this.io?.disconnect();
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.gl) {
            const ext = this.gl.getExtension('WEBGL_lose_context');
            if (ext) ext.loseContext();
            this.gl = null;
        }
    }

    applyBlendMode(mode) {
        if (!this.$canvas) return;
        this.$canvas.style.mixBlendMode = mode === 'normal' ? 'normal' : mode;
    }

    applyCssBlur(px) {
        if (!this.$canvas) return;
        this.$canvas.style.filter = px > 0 ? `blur(${px}px)` : '';
        this.$canvas.style.willChange = px > 0 ? 'filter' : '';
    }

    initGL() {
        const gl = this.$canvas.getContext('webgl', {
            premultipliedAlpha: true,
            antialias: false,
            alpha: true,
        });
        if (!gl) {
            this.$canvas.style.background = `var(--blob-color, #FFAAA5)`;
            this.$canvas.style.opacity = '0.25';
            return;
        }
        this.gl = gl;

        if (!this._contextHandlersBound) {
            this._contextHandlersBound = true;
            this.$canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); }, false);
            this.$canvas.addEventListener('webglcontextrestored', () => {
                this.gl = null;
                this.program = null;
                this.initGL();
                this.onResize();
            }, false);
        }

        const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
        const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
        if (!vs || !fs) return;

        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.warn('VoiceEmotion program link error', gl.getProgramInfoLog(program));
            return;
        }
        this.program = program;
        gl.useProgram(program);

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1,  1, -1,  -1, 1,
            -1,  1,  1, -1,   1, 1,
        ]), gl.STATIC_DRAW);
        const aPos = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

        const U = (n) => gl.getUniformLocation(program, n);
        this.uniforms = {
            u_time: U('u_time'), u_resolution: U('u_resolution'), u_color: U('u_color'),
            u_vig_radius: U('u_vig_radius'), u_vig_falloff: U('u_vig_falloff'),
            u_vig_skew: U('u_vig_skew'), u_vig_angle: U('u_vig_angle'),
            u_vig_pos_x: U('u_vig_pos_x'), u_vig_pos_y: U('u_vig_pos_y'),
            u_sine_freq: U('u_sine_freq'), u_sine_amp: U('u_sine_amp'), u_sine_speed: U('u_sine_speed'),
            u_shatter_scale: U('u_shatter_scale'), u_shatter_spread: U('u_shatter_spread'),
            u_shatter_angle: U('u_shatter_angle'), u_shatter_skew: U('u_shatter_skew'),
            u_bokeh_radius: U('u_bokeh_radius'), u_bokeh_tilt: U('u_bokeh_tilt'),
            u_bokeh_amount: U('u_bokeh_amount'), u_bokeh_iters: U('u_bokeh_iters'),
            u_radial_inner: U('u_radial_inner'), u_radial_outer: U('u_radial_outer'),
            u_power: U('u_power'), u_gamma: U('u_gamma'), u_brightness: U('u_brightness'),
        };

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.clearColor(0, 0, 0, 0);
    }

    onResize() {
        if (!this.$canvas) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const rect = this.$canvas.getBoundingClientRect();
        const w = Math.max(1, Math.round(rect.width  * dpr * 0.55));
        const h = Math.max(1, Math.round(rect.height * dpr * 0.55));
        if (this.$canvas.width !== w)  this.$canvas.width  = w;
        if (this.$canvas.height !== h) this.$canvas.height = h;
        if (this.gl) this.gl.viewport(0, 0, w, h);
    }

    onVoiceClick(event) {
        const color = event.currentTarget.getAttribute('data-voice-color');
        if (!color) return;
        this.targetColor = hexToRgb(color);
        this.$canvas?.style.setProperty('--blob-color', color);
    }

    onPointerMove(event) {
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top)  / rect.height;
        // The vignette's positionY in shader is bottom-up (gl coords). Flip Y
        // so the blob actually tracks the cursor visually.
        this.targetMouse.x = Math.max(0, Math.min(1, x));
        this.targetMouse.y = Math.max(0, Math.min(1, 1 - y));
    }

    tick(now) {
        if (!this.intersecting || this._suspended) { this.rafId = null; return; }
        this.rafId = requestAnimationFrame(this.tick);
        if (!this.gl || !this.program || !this.uniforms) return;
        if (this.lastTickMs == null) this.lastTickMs = now;
        const dt = (now - this.lastTickMs) / 1000;
        this.lastTickMs = now;
        this.elapsed += dt;

        const lerp = Math.min(1, dt * 4);
        for (let i = 0; i < 3; i++) {
            this.currentColor[i] += (this.targetColor[i] - this.currentColor[i]) * lerp;
        }

        // Ease mouse position toward target so the blob drifts smoothly.
        const mouseLerp = Math.min(1, dt * 3);
        this.currMouse.x += (this.targetMouse.x - this.currMouse.x) * mouseLerp;
        this.currMouse.y += (this.targetMouse.y - this.currMouse.y) * mouseLerp;

        const gl = this.gl;
        const u = this.uniforms;
        const p = this.params;
        gl.useProgram(this.program);
        gl.uniform1f(u.u_time, this.elapsed);
        gl.uniform2f(u.u_resolution, this.$canvas.width, this.$canvas.height);
        gl.uniform3f(u.u_color, this.currentColor[0], this.currentColor[1], this.currentColor[2]);

        gl.uniform1f(u.u_vig_radius, p.vigRadius);
        gl.uniform1f(u.u_vig_falloff, p.vigFalloff);
        gl.uniform1f(u.u_vig_skew, p.vigSkew);
        gl.uniform1f(u.u_vig_angle, p.vigAngle);
        // Mouse-driven blob center: lerp the vignette's pos_x / pos_y from
        // the configured defaults toward the eased cursor position.
        const mouseInfluence = 0.85;
        const posX = p.vigPosX + (this.currMouse.x - p.vigPosX) * mouseInfluence;
        const posY = p.vigPosY + (this.currMouse.y - p.vigPosY) * mouseInfluence;
        gl.uniform1f(u.u_vig_pos_x, posX);
        gl.uniform1f(u.u_vig_pos_y, posY);

        gl.uniform1f(u.u_sine_freq, p.sineFreq);
        gl.uniform1f(u.u_sine_amp, p.sineAmp);
        gl.uniform1f(u.u_sine_speed, p.sineSpeed);

        gl.uniform1f(u.u_shatter_scale, p.shatterScale);
        gl.uniform1f(u.u_shatter_spread, p.shatterSpread);
        gl.uniform1f(u.u_shatter_angle, p.shatterAngle);
        gl.uniform1f(u.u_shatter_skew, p.shatterSkew);

        gl.uniform1f(u.u_bokeh_radius, p.bokehRadius);
        gl.uniform1f(u.u_bokeh_tilt, p.bokehTilt);
        gl.uniform1f(u.u_bokeh_amount, p.bokehAmount);
        gl.uniform1i(u.u_bokeh_iters, Math.round(p.bokehIters));

        gl.uniform1f(u.u_radial_inner, p.radialInner);
        gl.uniform1f(u.u_radial_outer, p.radialOuter);
        gl.uniform1f(u.u_power, p.power);
        gl.uniform1f(u.u_gamma, p.gamma);
        gl.uniform1f(u.u_brightness, p.brightness);

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
}

customElements.define('block-voice-emotion', BlockVoiceEmotion);
